import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import useAuthStore from '../stores/useAuthStore';
import { useToast } from '../components/ToastContainer';
import axiosClient from '../services/api';
import useCourseStore from '../stores/useCourseStore';

const AdminCouponManagement = () => {
    const { user } = useAuthStore();
    const toast = useToast();

    const { courses, fetchCourses } = useCourseStore();

    const [coupons, setCoupons] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentCoupon, setCurrentCoupon] = useState(null);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        code: '',
        discountType: 'percentage',
        discountValue: '',
        appliesTo: 'all_courses',
        courses: [],
        maxUses: '',
        expiresAt: ''
    });

    const [allCourses, setAllCourses] = useState([]);

    const fetchCoupons = async () => {
        setIsLoading(true);
        try {
            const res = await axiosClient.get('/coupons');
            setCoupons(res.data.coupons);
        } catch (err) {
            toast.error('Failed to load coupons');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (courses) {
            setAllCourses(courses)
        }
    }, [courses]);

    useEffect(() => {
        fetchCoupons();
        fetchCourses();
    }, []);

    const resetForm = () => {
        setFormData({
            code: '',
            discountType: 'percentage',
            discountValue: '',
            appliesTo: 'all_courses',
            courses: [],
            maxUses: '',
            expiresAt: ''
        });
        setCurrentCoupon(null);
        setIsEditing(false);
    };

    const openCreateModal = () => {
        resetForm();
        setIsModalOpen(true);
    };

    const openEditModal = (coupon) => {
        setFormData({
            code: coupon.code,
            discountType: coupon.discountType,
            discountValue: coupon.discountValue,
            appliesTo: coupon.appliesTo,
            courses: coupon.courses?.map(c => c._id || c) || [],
            maxUses: coupon.maxUses || '',
            expiresAt: coupon.expiresAt ? new Date(coupon.expiresAt).toISOString().split('T')[0] : ''
        });
        setCurrentCoupon(coupon);
        setIsEditing(true);
        setIsModalOpen(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleCourseToggle = (courseId) => {
        setFormData(prev => {
            const courses = [...prev.courses];
            if (courses.includes(courseId)) {
                return { ...prev, courses: courses.filter(id => id !== courseId) };
            } else {
                return { ...prev, courses: [...courses, courseId] };
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const payload = {
                ...formData,
                discountValue: parseFloat(formData.discountValue),
                maxUses: formData.maxUses ? parseInt(formData.maxUses) : null,
                expiresAt: formData.expiresAt || null,
                courses: formData.appliesTo === 'specific_courses' ? formData.courses : []
            };

            if (isEditing && currentCoupon) {
                await axiosClient.put(`/coupons/${currentCoupon._id}`, {
                    ...payload,
                    courseIds: payload.courses,
                });

                toast.success('Coupon updated successfully!');
            } else {
                await axiosClient.post('/coupons', {
                    ...payload,
                    courseIds: payload.courses,
                });

                toast.success('Coupon created successfully!');
            }

            setIsModalOpen(false);
            fetchCoupons();
        } catch (err) {
            const msg = err.response?.data?.error || 'Operation failed';
            toast.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this coupon?')) return;
        try {
            await axiosClient.delete(`/coupons/${id}`);
            toast.success('Coupon deleted');
            fetchCoupons();
        } catch (err) {
            toast.error('Failed to delete coupon');
        }
    };

    return (
        <DashboardLayout userRole="admin">
            <div className="py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-8 flex justify-between items-center">
                        <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">Coupon Management</h1>
                        <button
                            onClick={openCreateModal}
                            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                        >
                            Create Coupon
                        </button>
                    </div>

                    {/* Coupons Table */}
                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-neutral-800 shadow-sm rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                                <thead className="bg-neutral-50 dark:bg-neutral-700">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">Code</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">Discount</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">Applies To</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">Uses</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">Expires</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>

                                <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                                    {/* Handle empty coupon list */}
                                    {(!coupons || coupons.length === 0) ? (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-8 text-center text-neutral-500 dark:text-neutral-400 ">
                                                <div className="mr-4 mt-1 mb-8">
                                                    <span className="text-6xl">🎟️</span>
                                                </div>
                                                No coupons created yet. Create one to get started.
                                            </td>
                                        </tr>
                                    ) : (
                                        coupons.map(coupon => (
                                            <tr key={coupon._id}>
                                                <td className="px-6 py-4 whitespace-nowrap font-medium dark:text-neutral-300">{coupon.code}</td>
                                                <td className="px-6 py-4 whitespace-nowrap dark:text-neutral-300">
                                                    {coupon.discountType === 'percentage'
                                                        ? `${coupon.discountValue}%`
                                                        : `₦${Number(coupon.discountValue).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`
                                                    }
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap dark:text-neutral-300">
                                                    {coupon.appliesTo === 'all_courses' ? 'All Courses' : `${coupon.courses.length} courses`}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap dark:text-neutral-300">
                                                    {coupon.usedCount} / {coupon.maxUses || '∞'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap dark:text-neutral-300">
                                                    {coupon.expiresAt ? new Date(coupon.expiresAt).toLocaleDateString() : 'Never'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <button
                                                        onClick={() => openEditModal(coupon)}
                                                        className="text-primary-600 hover:text-primary-900 dark:text-primary-400 mr-4"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(coupon._id)}
                                                        className="text-danger-600 hover:text-danger-900 dark:text-danger-400"
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                </div>

                {/* Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block">
                            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setIsModalOpen(false)}></div>
                            <div className="inline-block align-bottom bg-white dark:bg-neutral-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                                <div className="bg-white dark:bg-neutral-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
                                        {isEditing ? 'Edit Coupon' : 'Create New Coupon'}
                                    </h3>
                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Coupon Code</label>
                                            <input
                                                type="text"
                                                name="code"
                                                value={formData.code}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                required
                                                disabled={isEditing}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Discount Type</label>
                                                <select
                                                    name="discountType"
                                                    value={formData.discountType}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                    required
                                                >
                                                    <option value="percentage">Percentage</option>
                                                    <option value="fixed">Fixed Amount</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                                    {formData.discountType === 'percentage' ? 'Percentage (%)' : 'Amount (₦)'}
                                                </label>
                                                <input
                                                    type="number"
                                                    name="discountValue"
                                                    value={formData.discountValue}
                                                    onChange={handleInputChange}
                                                    min="0"
                                                    step={formData.discountType === 'percentage' ? '0.1' : '1'}
                                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Applies To</label>
                                            <select
                                                name="appliesTo"
                                                value={formData.appliesTo}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            >
                                                <option value="all_courses">All Courses</option>
                                                <option value="specific_courses">Specific Courses</option>
                                            </select>
                                        </div>
                                        {formData.appliesTo === 'specific_courses' && (
                                            <div className="max-h-40 overflow-y-auto border border-neutral-200 dark:border-neutral-700 rounded p-2">
                                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Select Courses</label>
                                                {allCourses.map(course => (
                                                    <div key={course._id} className="flex items-center mb-1">
                                                        <input
                                                            type="checkbox"
                                                            id={`course-${course._id}`}
                                                            checked={formData.courses.includes(course._id)}
                                                            onChange={() => handleCourseToggle(course._id)}
                                                            className="mr-2"
                                                        />
                                                        <label htmlFor={`course-${course._id}`} className="text-sm">{course.title} (₦{Number(course.price).toLocaleString('en-NG', { minimumFractionDigits: 2 })})</label>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Max Uses (optional)</label>
                                                <input
                                                    type="number"
                                                    name="maxUses"
                                                    value={formData.maxUses}
                                                    onChange={handleInputChange}
                                                    min="1"
                                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Expiry Date</label>
                                                <input
                                                    type="date"
                                                    name="expiresAt"
                                                    value={formData.expiresAt}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex justify-end space-x-3 pt-4">
                                            <button
                                                type="button"
                                                onClick={() => setIsModalOpen(false)}
                                                className="px-4 py-2 text-neutral-700 dark:text-neutral-300 rounded"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className={`px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600 flex items-center justify-center ${isSubmitting && 'opacity-70 cursor-not-allowed'}`}
                                            >
                                                {isSubmitting ? (
                                                    <div className="flex items-center">
                                                        <div className="animate-spin h-4 w-4 border-b-2 border-white mr-2"></div>
                                                        {isEditing ? 'Updating...' : 'Creating...'}
                                                    </div>
                                                ) : (
                                                    isEditing ? 'Update' : 'Create'
                                                )}
                                            </button>

                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default AdminCouponManagement;